import { ComponentProps, FC } from 'react'
import { unstable_useBlocker as useBlocker } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { AreaChartWidget, ChartWidgetEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import {
  ContractVersionInsightFragment,
  SalaryInsightDocument,
  SalaryInsightQuery,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import orderBy from 'lodash/orderBy'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getLegalEntityCurrency } from 'modules/core/lib/formatCurrency'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { salaryFrequency } from 'modules/contracts/lib/frequencies'
import { formatNumberForAxis } from 'modules/profile/lib/utils'

import {
  CENTS,
  MONTHS_PER_YEAR,
  getContractVersionInMonth,
  getXTicksFromMonth,
  legacyCompensationsAmount,
  newCompensationsAmount,
} from './helpers'

const emptyWidget = {
  title: '',
  currentSalaryAmountInCents: 0,
  currentSalaryFrequency: 'yearly',
  currentSalaryCurrency: 'EUR',
  link: { title: '', url: '' },
  chartData: [],
  shouldShow: false,
  recommendToAddSalary: false,
}

const validSalaryFrequencies = ['yearly', 'monthly']

const chartOffset = 5e3

const getEstimatedBaseMonthlySalaryInCents = (contract: ContractVersionInsightFragment) => {
  const salaryAmount = Number(contract.salaryAmount) || 0

  if (contract.salaryFrequency === 'yearly') {
    return salaryAmount / MONTHS_PER_YEAR
  }

  if (contract.salaryFrequency === 'monthly') {
    return salaryAmount
  }

  return 0
}

// Internal function that accepts currentDate for testing purposes
export const prepareDataWithDate = (data: SalaryInsightQuery, currentDate: string) => {
  // Check if company has the new compensations management feature
  const hasNewCompensationsFeature = data?.companies?.featureAvailabilitiesConnection?.nodes?.some(
    (feature) => feature.id === Features.compensations_management && feature.available
  )

  const contracts = orderBy(
    data?.contracts.contractVersionsConnection.nodes.filter(
      (contractVersion) => contractVersion.salaryAmount
    ),
    ['effectiveOn'],
    ['asc']
  )

  const canShowWidget = contracts.every(
    (contractVersion) =>
      contractVersion.salaryFrequency &&
      validSalaryFrequencies.includes(contractVersion.salaryFrequency)
  )

  if (!contracts.length || !canShowWidget)
    return {
      ...emptyWidget,
      recommendToAddSalary: !contracts.length,
    }

  const compensationsAmount = (
    contractVersion: ContractVersionInsightFragment,
    monthDate: string
  ) => {
    if (hasNewCompensationsFeature) {
      return newCompensationsAmount(contractVersion, monthDate)
    }
    return legacyCompensationsAmount(contractVersion, monthDate)
  }

  const extractMonthShortString = (date: string) => {
    return new Date(date).toLocaleString('default', { month: 'long', year: 'numeric' })
  }

  const firstContractDate = contracts?.[0]?.effectiveOn

  const xTicks = firstContractDate ? getXTicksFromMonth(firstContractDate, currentDate) : []

  const chartData = compact(
    xTicks.map((date) => {
      const contract = getContractVersionInMonth(contracts, date)

      // Chart shows all decimal places for numbers below 1000 and corrupts the chart widget width
      // So we show only integral parts of the numbers using @Math.trunc()
      const baseSalary = contract
        ? Math.trunc(getEstimatedBaseMonthlySalaryInCents(contract) / CENTS)
        : 0
      const additionalCompensations = contract
        ? Math.trunc(compensationsAmount(contract, date) / CENTS)
        : 0
      const totalSalary = Math.trunc(baseSalary + additionalCompensations)
      return {
        label: extractMonthShortString(date),
        values: {
          baseSalary,
          additionalCompensations,
          totalSalary,
        },
        dashed: true,
      }
    })
  )

  const lastDate = xTicks?.[xTicks.length - 1]

  if (!lastDate) return { ...emptyWidget, recommendToAddSalary: true }

  const currentContract = getContractVersionInMonth(contracts, lastDate)

  const employee = data.employees.employee
  const employeeId = employee?.id
  const employeeName = employee?.preferredName || employee?.firstName
  const legalEntityId = employee?.employeeGroup?.legalEntityId

  // Calculate monthly amounts for the last data point (current salary)
  const baseSalary = currentContract ? getEstimatedBaseMonthlySalaryInCents(currentContract) : 0
  const additionalCompensationsInCents = currentContract
    ? compensationsAmount(currentContract, lastDate)
    : 0
  const currentSalaryAmountInCents =
    baseSalary || additionalCompensationsInCents
      ? baseSalary + additionalCompensationsInCents
      : undefined

  const legacyCompensationCurrency = !hasNewCompensationsFeature
    ? currentContract?.compensationsConnection.nodes?.[0]?.currency
    : null
  const currentSalaryCurrency =
    legacyCompensationCurrency ??
    (legalEntityId ? getLegalEntityCurrency(legalEntityId) : null) ??
    employee?.company.currency

  // Always show monthly frequency in the title
  const currentSalaryFrequency = salaryFrequency('monthly')

  return {
    title: i18n.t('insights.widgets.salary.title'),
    currentSalaryAmountInCents,
    currentSalaryCurrency,
    currentSalaryFrequency,
    chartData,
    shouldShow: true,
    recommendToAddSalary: false,
    employeeId,
    employeeName,
    legalEntityId,
  }
}

// Public function that matches the widget system's expected signature
export const prepareData = (data: SalaryInsightQuery, _timeInMs?: number) =>
  prepareDataWithDate(data, new Date().toISOString())

type PreparedData = ReturnType<typeof prepareDataWithDate>

const useLink = ({ employeeId }: { employeeId?: number }) => {
  const { employee } = useCurrentSession()

  const currentEmployeeId = employee?.id

  const { navigation } = useNavigation()

  const ownEmployeeLink = navigation.profile.contractVersions.fullPath()
  const otherEmployeeLink = employeeId
    ? navigation.employees.id.contractVersions.fullPath({
        id: employeeId,
      })
    : undefined

  return currentEmployeeId === employeeId ? ownEmployeeLink : otherEmployeeLink
}

const SalaryWidgetComponent: FC<{
  data: PreparedData
  employeeId: number
  onClickWidget?: () => void
}> = ({ data, employeeId, onClickWidget }) => {
  const { title, chartData, currentSalaryAmountInCents, currentSalaryFrequency, legalEntityId } =
    data

  const linkUrl = useLink({
    employeeId,
  })

  useBlocker(({ nextLocation }) => {
    if (linkUrl?.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  const link = linkUrl
    ? {
        title,
        url: linkUrl,
      }
    : undefined

  const currency = currentSalaryAmountInCents
    ? formatCurrency(currentSalaryAmountInCents, undefined, null, legalEntityId)
    : undefined

  const frequency = currentSalaryFrequency

  const comment = currency && frequency ? `${currency} ${frequency}` : currency

  const chart: ComponentProps<typeof AreaChartWidget>['chart'] = {
    data: chartData ?? [],
    lineType: 'linear',
    xAxis: {
      tickFormatter: () => '',
    },
    yAxis: {
      tickFormatter: formatNumberForAxis,
      domain: [
        Math.max(Math.min(...chartData.map((c) => c.values.baseSalary)) - chartOffset, 0),
        Math.max(...chartData.map((c) => c.values.totalSalary)) + chartOffset,
      ],
    },
    dataConfig: {
      totalSalary: {
        label: i18n.t('insights.widgets.salary.labels.total_salary'),
        color: 'categorical-1',
      },
      baseSalary: {
        label: i18n.t('insights.widgets.salary.labels.base_salary'),
        color: 'categorical-3',
        dashed: true,
      },
      additionalCompensations: {
        label: i18n.t('insights.widgets.salary.labels.additional_compensations'),
        color: 'categorical-5',
        dashed: true,
      },
    },
    blurArea: 'l',
    marginTop: 12,
  }

  return (
    <AreaChartWidget
      header={{
        title,
        comment,
        link,
      }}
      chart={chart}
      canBeBlurred
    />
  )
}

const SalaryEmptyComponent: FC<{ data: PreparedData; onClickWidget?: () => void }> = ({
  data,
  onClickWidget,
}) => {
  const navigate = useNavigate()

  const { employeeId, employeeName } = data

  const linkUrl = useLink({
    employeeId,
  })

  useBlocker(({ nextLocation }) => {
    if (linkUrl?.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  if (data.recommendToAddSalary && employeeName) {
    return (
      <ChartWidgetEmptyState
        title={i18n.t('insights.widgets.salary.title')}
        content={i18n.t('insights.widgets.salary.empty_states.no_salary.content', {
          name: employeeName,
        })}
        buttonLabel={i18n.t('insights.widgets.salary.empty_states.no_salary.add_info')}
        buttonAction={
          linkUrl
            ? () => {
                navigate(linkUrl)
              }
            : undefined
        }
        type="line-chart"
      />
    )
  }
}

export const buildSalaryWidget = () =>
  buildWidget({
    id: 'salary',
    document: SalaryInsightDocument,
    prepareData,
    isEmpty: (data) => !data.shouldShow,
    emptyComponent: SalaryEmptyComponent,
    component: SalaryWidgetComponent,
    policies: ['seeSalary'],
  })
